/**
 * Team LLM models (`/v1/llm/configs`).
 *
 * Each row = credentials (encrypted) + vendor kind + API model id + parameters.
 * Agents bind via `AgentSpec.llm_config_id`. Only `team_admin` mutates.
 */

import { Loader2, Pencil, Plus, Trash2, Zap } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { EmptyState, ErrorBanner } from "@/components/empty-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { readLlmConfigListItems } from "@/lib/agent-llm-preset";
import { ApiError, api } from "@/lib/api";
import {
  LLM_VENDOR_KINDS,
  type LLMConfigCreateRequest,
  type LLMConfigListResponse,
  type LLMConfigSummary,
  type LLMConfigUpdateRequest,
  type LlmModelTestResponse,
  type LlmVendorKind,
} from "@/lib/types";

interface LLMConfigsCardProps {
  isAdmin: boolean;
  title?: string;
  description?: string;
}

export function LLMConfigsCard({
  isAdmin,
  title = "Models",
  description = "Each row is one LLM connection + API model id + parameters. Add the API key here; agents only pick this row by id in Setup.",
}: LLMConfigsCardProps) {
  const [configs, setConfigs] = useState<LLMConfigSummary[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<LLMConfigSummary | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [testingId, setTestingId] = useState<string | null>(null);

  useEffect(() => {
    void refresh();
  }, []);

  async function refresh() {
    try {
      const cfgs = await api<LLMConfigListResponse>("/llm/configs");
      setLoadError(null);
      const cfgsParsed = readLlmConfigListItems(cfgs);
      if (cfgsParsed.badShape) {
        setConfigs([]);
        setLoadError("Unexpected /llm/configs response — try refresh.");
        return;
      }
      setConfigs(cfgsParsed.items);
    } catch (err) {
      setConfigs([]);
      setLoadError(err instanceof Error ? err.message : "Failed to load models");
    }
  }

  async function handleTest(cfg: LLMConfigSummary) {
    setTestingId(cfg.id);
    try {
      const r = await api<LlmModelTestResponse>(`/llm/configs/${cfg.id}/test`, {
        method: "POST",
        body: JSON.stringify({}),
      });
      if (r.ok) {
        const sample = r.sample ? ` · "${r.sample.trim()}"` : "";
        toast.success(`Connected · ${r.latency_ms} ms · ${r.model ?? "?"}${sample}`);
      } else {
        toast.error(r.error ?? "Connectivity test failed");
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Connectivity test failed");
    } finally {
      setTestingId(null);
    }
  }

  async function handleDelete(cfg: LLMConfigSummary) {
    if (
      !confirm(
        `Delete model "${cfg.slug}"? Agents bound to it will need to pick another model first.`,
      )
    ) {
      return;
    }
    setDeletingId(cfg.id);
    try {
      await api(`/llm/configs/${cfg.id}`, { method: "DELETE" });
      toast.success(`Deleted "${cfg.slug}"`);
      await refresh();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        const refs = (err.details?.referenced_by as string[] | undefined) ?? [];
        toast.error(
          refs.length
            ? `In use by ${refs.length} agent(s) — rebind them first.`
            : (err.message ?? "Cannot delete: model still in use."),
        );
      } else {
        toast.error(err instanceof Error ? err.message : "Failed to delete model");
      }
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <Card id="llm-models-card">
      <CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0">
        <div>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </div>
        {isAdmin ? (
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button size="sm">
                <Plus /> Add model
              </Button>
            </DialogTrigger>
            <ConfigFormDialog
              mode="create"
              onSubmitted={() => {
                setCreateOpen(false);
                void refresh();
              }}
            />
          </Dialog>
        ) : null}
      </CardHeader>
      <CardContent className="space-y-3">
        {loadError ? <ErrorBanner>{loadError}</ErrorBanner> : null}
        {!isAdmin ? (
          <p className="text-xs text-muted-foreground">
            Read-only — only team admins can add, edit, or delete models.
          </p>
        ) : null}
        {configs === null ? (
          <div className="space-y-2">
            <Skeleton className="h-8" />
            <Skeleton className="h-8" />
          </div>
        ) : configs.length === 0 ? (
          <EmptyState>
            No models yet.{" "}
            {isAdmin
              ? "Add one — include vendor, base URL (if any), API key, and the API model id your vendor expects."
              : null}
          </EmptyState>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Slug</TableHead>
                <TableHead>Kind</TableHead>
                <TableHead>API model id</TableHead>
                <TableHead>Credential</TableHead>
                <TableHead className="text-right">Temp</TableHead>
                <TableHead className="text-right">Max tokens</TableHead>
                {isAdmin ? <TableHead aria-label="actions" /> : null}
              </TableRow>
            </TableHeader>
            <TableBody>
              {configs.map((c) => (
                <TableRow key={c.id}>
                  <TableCell className="font-mono text-xs">
                    {c.slug}
                    {c.display_name ? (
                      <div className="text-[11px] text-muted-foreground">{c.display_name}</div>
                    ) : null}
                  </TableCell>
                  <TableCell className="text-xs">
                    <Badge variant="outline" className="text-[10px]">
                      {c.provider_kind}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs">
                    <code>{c.model}</code>
                  </TableCell>
                  <TableCell className="text-xs">
                    {c.has_secret ? (
                      <span className="text-emerald-600">key stored</span>
                    ) : (
                      <span className="text-amber-600">no key</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right text-xs tabular-nums">
                    {c.temperature.toFixed(2)}
                  </TableCell>
                  <TableCell className="text-right text-xs tabular-nums">
                    {c.max_output_tokens ?? "—"}
                  </TableCell>
                  {isAdmin ? (
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={`Test ${c.slug}`}
                        disabled={
                          testingId === c.id ||
                          !c.has_secret ||
                          !["openai", "openai-compatible", "anthropic"].includes(c.provider_kind)
                        }
                        onClick={() => void handleTest(c)}
                        title="Ping vendor (OpenAI / Anthropic only)"
                      >
                        {testingId === c.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Zap className="h-4 w-4" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={`Edit ${c.slug}`}
                        onClick={() => setEditing(c)}
                      >
                        <Pencil />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={`Delete ${c.slug}`}
                        disabled={deletingId === c.id}
                        onClick={() => handleDelete(c)}
                      >
                        {deletingId === c.id ? (
                          <Loader2 className="animate-spin" />
                        ) : (
                          <Trash2 className="text-destructive" />
                        )}
                      </Button>
                    </TableCell>
                  ) : null}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <Dialog open={editing !== null} onOpenChange={(open) => (open ? null : setEditing(null))}>
        {editing ? (
          <ConfigFormDialog
            mode="edit"
            config={editing}
            onSubmitted={() => {
              setEditing(null);
              void refresh();
            }}
          />
        ) : null}
      </Dialog>
    </Card>
  );
}

interface ConfigFormProps {
  mode: "create" | "edit";
  config?: LLMConfigSummary;
  onSubmitted: () => void;
}

function ConfigFormDialog({ mode, config, onSubmitted }: ConfigFormProps) {
  const [kind, setKind] = useState<LlmVendorKind>(
    (config?.provider_kind as LlmVendorKind) ?? "openai",
  );
  const [slug, setSlug] = useState(config?.slug ?? "");
  const [displayName, setDisplayName] = useState(config?.display_name ?? "");
  const [baseUrl, setBaseUrl] = useState(config?.base_url ?? "");
  const [secret, setSecret] = useState("");
  const [model, setModel] = useState(config?.model ?? "");
  const [temperature, setTemperature] = useState(String(config?.temperature ?? 0.7));
  const [maxTokens, setMaxTokens] = useState(
    config?.max_output_tokens != null ? String(config.max_output_tokens) : "",
  );
  const [submitting, setSubmitting] = useState(false);

  const title = mode === "create" ? "Add model" : `Edit model "${config?.slug}"`;
  const submitLabel = mode === "create" ? "Create" : "Save changes";

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (submitting) return;
    const trimmedSlug = slug.trim();
    if (!trimmedSlug) {
      toast.error("Slug is required");
      return;
    }
    if (!model.trim()) {
      toast.error("Model is required");
      return;
    }
    const tempNum = Number.parseFloat(temperature);
    if (!Number.isFinite(tempNum) || tempNum < 0 || tempNum > 2) {
      toast.error("Temperature must be a number between 0 and 2");
      return;
    }
    const maxTokensNum = maxTokens.trim() ? Number.parseInt(maxTokens, 10) : null;
    if (maxTokensNum !== null && (!Number.isFinite(maxTokensNum) || maxTokensNum <= 0)) {
      toast.error("Max output tokens must be a positive integer or empty");
      return;
    }

    setSubmitting(true);
    try {
      if (mode === "create") {
        if (!secret.trim()) {
          toast.error("API key is required when creating a model");
          setSubmitting(false);
          return;
        }
        const body: LLMConfigCreateRequest = {
          slug: trimmedSlug,
          kind,
          display_name: displayName.trim() ? displayName.trim() : null,
          base_url: baseUrl.trim() ? baseUrl.trim() : null,
          secret: secret.trim(),
          model: model.trim(),
          temperature: tempNum,
          max_output_tokens: maxTokensNum,
        };
        await api("/llm/configs", { method: "POST", body: JSON.stringify(body) });
        toast.success(`Added "${trimmedSlug}"`);
      } else {
        if (!config) {
          toast.error("Edit dialog opened without a config");
          return;
        }
        const body: LLMConfigUpdateRequest = {};
        if (kind !== config.provider_kind) body.kind = kind;
        if ((displayName.trim() || null) !== (config.display_name ?? null)) {
          body.display_name = displayName.trim() ? displayName.trim() : null;
        }
        if ((baseUrl.trim() || null) !== (config.base_url ?? null)) {
          body.base_url = baseUrl.trim() ? baseUrl.trim() : null;
        }
        if (secret.trim()) body.secret = secret.trim();
        if (model.trim() !== config.model) body.model = model.trim();
        if (tempNum !== config.temperature) body.temperature = tempNum;
        if (maxTokensNum !== config.max_output_tokens) body.max_output_tokens = maxTokensNum;
        if (Object.keys(body).length === 0) {
          toast.message("Nothing changed");
          setSubmitting(false);
          return;
        }
        await api(`/llm/configs/${config.id}`, {
          method: "PATCH",
          body: JSON.stringify(body),
        });
        toast.success(`Updated "${config.slug}"`);
      }
      onSubmitted();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save model");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>
          One row = credentials + vendor + API model id. Slug is what legacy{" "}
          <code className="text-[10px]">spec.model.provider</code> may reference after migration.
        </DialogDescription>
      </DialogHeader>
      <form onSubmit={onSubmit} className="space-y-3">
        <div className="space-y-1.5">
          <Label htmlFor="cfg-kind">Vendor / kind</Label>
          <Select value={kind} onValueChange={(v) => setKind(v as LlmVendorKind)}>
            <SelectTrigger id="cfg-kind">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LLM_VENDOR_KINDS.map((k) => (
                <SelectItem key={k} value={k}>
                  {k}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="cfg-slug">Slug</Label>
            <Input
              id="cfg-slug"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="openai-prod"
              maxLength={64}
              pattern="[a-zA-Z0-9_\-\.]+"
              disabled={mode === "edit"}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cfg-display">Display name</Label>
            <Input
              id="cfg-display"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              maxLength={120}
              placeholder="Production OpenAI"
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="cfg-base">Base URL (optional)</Label>
          <Input
            id="cfg-base"
            value={baseUrl}
            onChange={(e) => setBaseUrl(e.target.value)}
            placeholder="https://api.openai.com/v1"
            className="font-mono text-xs"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="cfg-secret">API key / secret</Label>
          <Input
            id="cfg-secret"
            type="password"
            autoComplete="off"
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            placeholder={mode === "edit" ? "Leave blank to keep current key" : "sk-…"}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="cfg-model">API model id</Label>
          <Input
            id="cfg-model"
            value={model}
            onChange={(e) => setModel(e.target.value)}
            placeholder="gpt-4o-mini"
            className="font-mono"
            required
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="cfg-temp">Temperature</Label>
            <Input
              id="cfg-temp"
              type="number"
              step="0.1"
              min="0"
              max="2"
              value={temperature}
              onChange={(e) => setTemperature(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cfg-maxtok">Max output tokens</Label>
            <Input
              id="cfg-maxtok"
              type="number"
              min="1"
              value={maxTokens}
              onChange={(e) => setMaxTokens(e.target.value)}
              placeholder="(none)"
            />
          </div>
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" type="button">
              Cancel
            </Button>
          </DialogClose>
          <Button type="submit" disabled={submitting}>
            {submitting ? <Loader2 className="animate-spin" /> : null}
            {submitLabel}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
